import { createRouter, createWebHistory } from 'vue-router';
import { useGoogleAnalytics } from '../services/analytics/useGoogleAnalytics';
import { useAppConfigStore } from '@stores/appConfig.store';
import { isValidRateRouteId, normalizeRateRouteId } from '../utils/rateRouteId';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('../views/HomeView.vue'),
    },
    {
      path: '/search',
      name: 'search',
      meta: { fallback: '/' },
      props: { section: 'list' },
      component: () => import('../views/SearchView.vue'),
    },
    {
      path: '/room/details/:id',
      name: 'room details',
      meta: { fallback: '/search' },
      props: { section: 'roomDetails' },
      component: () => import('../views/SearchView.vue'),
    },
    {
      path: '/rate/details/:id',
      name: 'rate details',
      meta: { fallback: '/search' },
      props: { section: 'rateDetails' },
      component: () => import('../views/SearchView.vue'),
    },
    {
      path: '/extras/:id',
      name: 'extras',
      meta: { fallback: '/search' },
      props: { section: 'extras' },
      component: () => import('../views/CheckoutView.vue'),
    },
    {
      path: '/payment',
      name: 'payment',
      meta: { fallback: '/search' },
      props: { section: 'payment' },
      component: () => import('../views/CheckoutView.vue'),
    },
    {
      path: '/payment/mbway',
      name: 'mbway',
      props: { section: 'mbway' },
      component: () => import('../views/CheckoutView.vue'),
    },
    {
      path: '/guest',
      name: 'guest details',
      props: { section: 'guest' },
      component: () => import('../views/CheckoutView.vue'),
    },
    {
      path: '/confirmation',
      name: 'confirmation',
      props: { section: 'confirmation' },
      component: () => import('../views/CheckoutView.vue'),
    },
    {
      path: '/agent',
      name: 'agent',
      component: () => import('../views/AgentView.vue'),
    },
    {
      path: '/playerLogin',
      name: 'playerLogin',
      component: { template: '<div></div>' },
    },
  ],
});

let currentRoute = null;
let cachedHotelId = null;

// Tracks the canonical path so both id formats (e.g. "p23r5" and "23.5")
// report as the same page in analytics.
const trackCurrentRoute = (hotelId) => {
  if (currentRoute && hotelId) {
    const { trackPageView } = useGoogleAnalytics();
    let path = currentRoute.fullPath;
    const rawId = currentRoute.params?.id;
    if (
      (currentRoute.name === 'rate details' ||
        currentRoute.name === 'room details') &&
      typeof rawId === 'string' &&
      rawId
    ) {
      const normalizedId = normalizeRateRouteId(rawId);
      if (normalizedId !== rawId) {
        path = path.replace(rawId, normalizedId);
      }
    }
    trackPageView(path, hotelId);
  }
};

export const trackInitialPageView = (hotelId) => {
  if (hotelId) {
    cachedHotelId = hotelId;

    if (currentRoute) {
      trackCurrentRoute(hotelId);
    }
  }
};

const DETAILS_ROUTE_NAMES = ['room details', 'rate details'];

// Crawlers open placeholder paths such as /room/details/roomRateId. A details
// route whose id is not numeric goes to the results page, keeping the query
// (apikey, channelKey, dates, currency...), instead of asking CRS for it.
export const invalidDetailsRouteRedirect = (to) => {
  if (
    DETAILS_ROUTE_NAMES.includes(to.name) &&
    !isValidRateRouteId(to.params?.id)
  ) {
    return { name: 'search', query: to.query, hash: to.hash, replace: true };
  }
  return null;
};

router.beforeEach((to, from, next) => {
  const detailsRedirect = invalidDetailsRouteRedirect(to);
  if (detailsRedirect) {
    next(detailsRedirect);
    return;
  }

  const isInitialLoad = from.matched.length === 0;
  if (
    isInitialLoad &&
    to.name === 'home' &&
    to.query.startDay &&
    to.query.nrNights
  ) {
    next({ name: 'search', query: to.query, replace: true });
    return;
  }

  const inIframe = typeof window !== 'undefined' && window.self !== window.top;
  const cameBackFromPayment = to.query.backFromEpg;
  const isClientNavigation = from.matched?.length > 0;

  if (inIframe && cameBackFromPayment && isClientNavigation) {
    const url = window.location.origin + router.resolve(to).href;
    window.location.href = url;
    next(false);
    return;
  }

  next();
});

router.afterEach((to) => {
  currentRoute = to;

  const appConfigStore = useAppConfigStore();
  const { applicationSetup } = appConfigStore;

  const hotelId = cachedHotelId || applicationSetup.value?.hotel_id;

  if (hotelId) {
    trackCurrentRoute(hotelId);

    if (!cachedHotelId) {
      cachedHotelId = hotelId;
    }
  }
});

export default router;
